import React, { useEffect, useMemo, useState } from 'react';
import {  FaUsers, FaFileAlt, FaCog, FaClock, FaArrowRight, FaCoins, FaCheckCircle  } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../components/Config/api.service';
import { canAccessSettings, canEditHrData, canViewEmployees } from '../../components/Auth/permissions';
import { AiOutlineMan, AiOutlineWoman  } from "react-icons/ai";
import FullCalendar from '@fullcalendar/react';
import MyCalendar, { toYearMonth } from '../Calendar/MyCalendar';
import NoContractAlert from './NoContractAlert';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import Holidays from 'date-holidays';
import { useCountryConfig } from '../../components/Config/useCountryConfig';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
ChartJS.register(ArcElement, Tooltip, Legend);
function Home() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'fr-FR';
  const navigate = useNavigate();
  const [activePayrollMonth, setActivePayrollMonth] = useState(toYearMonth(new Date()));
  const [isCurrentMonthLocked, setIsCurrentMonthLocked] = useState(false);
  // Mois affiché par le calendrier : les chiffres et les événements le suivent
  // (Yros/payor_enterprise#263). Il démarre sur le mois de paie actif.
  const [dashboardMonth, setDashboardMonth] = useState(null);
  const [stats, setStats] = useState({
    totalEmployees: 0,
    maleEmployees: 0,
    femaleEmployees: 0,
    currentAbsences: 0,
    totalPayrollCost: 0,
    total_brut: 0,
    total_salariale_globale_prev_month: 0,
    total_impots: 0,
    total_cotisation_salariale: 0,
    total_cotisation_patronale: 0,
    payslipCount: null,
    employeesWithoutContract: 0,
  });
  // Keep a place for custom events (company-specific). Holidays will be pulled from date-holidays.
  const [selectedDates, setSelectedDates] = useState([]);
  const countryConfig = useCountryConfig();

  const shownMonth = dashboardMonth || activePayrollMonth;
  const [shownYear, shownMonthIndex] = useMemo(() => {
    const [year, month] = shownMonth.split('-').map(Number);
    return [year, month - 1];
  }, [shownMonth]);
  const isShowingCurrentMonth = shownMonth === toYearMonth(new Date());

  const calendarEvents = useMemo(() => {
    const hd = new Holidays((countryConfig && countryConfig.taxSystem) || 'sn');
    // gather holidays for the shown year (and adjacent years if needed)
    const years = [shownYear - 1, shownYear, shownYear + 1];
    const events = [];

    years.forEach((y) => {
      try {
        const holidays = hd.getHolidays(y) || [];
        holidays.forEach((h) => {
          if (!h || !h.date) return;
          const d = new Date(h.date);
          if (Number.isNaN(d.getTime())) return;

          events.push({
            title: h.name || t('home.publicHoliday'),
            start: d.toISOString().split('T')[0],
            color: h.type === 'public' ? '#eab308' : '#f97316',
            raw: h
          });
        });
      } catch (err) {
        // ignore errors per year
      }
    });

    // Only keep events of the month shown by the calendar
    return events.filter((e) => {
      const ed = new Date(`${e.start}T00:00:00`);
      return ed.getMonth() === shownMonthIndex && ed.getFullYear() === shownYear;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryConfig, i18n.resolvedLanguage, shownYear, shownMonthIndex]);

  const upcomingEvents = useMemo(() => {
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    // Merge custom selectedDates with calendar-derived events of the shown month.
    // Only the current month is limited to upcoming events.
    const allEvents = [...(selectedDates || []), ...(calendarEvents || [])];
    return allEvents
      .map((event) => {
        const eventDate = new Date(`${event.start}T00:00:00`);
        const daysUntil = Math.ceil((eventDate - startOfToday) / (1000 * 60 * 60 * 24));

        return {
          ...event,
          eventDate,
          daysUntil
        };
      })
      .filter((e) => {
        if (typeof e.daysUntil !== 'number') return false;
        // Upcoming only (today or future) for the current month
        if (isShowingCurrentMonth && e.daysUntil < 0) return false;

        // Must be in the month shown by the calendar
        return (
          e.eventDate.getMonth() === shownMonthIndex &&
          e.eventDate.getFullYear() === shownYear
        );
      })
      .sort((left, right) => left.eventDate - right.eventDate)
      .slice(0, 4);
  }, [selectedDates, calendarEvents, isShowingCurrentMonth, shownMonthIndex, shownYear]);

  const formatEventDate = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleDateString(dateLocale, {
      day: 'numeric',
      month: 'short'
    });
  };

  const shiftMonth = (yearMonth, delta) => {
    const [year, month] = yearMonth.split('-').map(Number);
    const shiftedDate = new Date(year, month - 1 + delta, 1);
    return `${shiftedDate.getFullYear()}-${String(shiftedDate.getMonth() + 1).padStart(2, '0')}`;
  };

  const formatMonthLabel = (yearMonth) => {
    const [year, month] = yearMonth.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' });
  };

  useEffect(() => {
    const resolveActivePayrollMonth = async () => {
      const currentMonth = toYearMonth(new Date());
      const previousMonth = shiftMonth(currentMonth, -1);
      let targetMonth = currentMonth;
      let locked = false;

      try {
        const response = await apiService.getElement('/payslip/active_month');
        if (response?.data?.active_month) {
          targetMonth = response.data.active_month;
          locked = response.data.locked;
        }
      } catch (error) {
        console.error('Erreur lors de la récupération du mois actif de paie:', error);
      }

      setActivePayrollMonth(targetMonth);
      setIsCurrentMonthLocked(locked);
      // Sauf si l'utilisateur a déjà changé de mois entre-temps.
      setDashboardMonth((month) => month || targetMonth);
    };

    resolveActivePayrollMonth();
  }, []);

  // Memoize date-related calculations
  const dateInfo = useMemo(() => {
    const today = new Date();
  // Helper function to capitalize first letter
  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);
  // Le bandeau suit le mois affiché par le calendrier (Yros/payor_enterprise#263).
  const [year, month] = shownMonth.split('-').map(Number);
  const payrollDate = new Date(year, month - 1, 1);

  // Get month names and capitalize them
  const currentMonth = capitalize(payrollDate.toLocaleDateString(dateLocale, { month: 'long' }));
  const currentMonthLabel = capitalize(payrollDate.toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' }));


    const fullDate = today.toLocaleDateString(dateLocale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    // Calculate next month properly
    const nextMonthIndex = payrollDate.getMonth() === 11 ? 0 : payrollDate.getMonth() + 1;
    const nextMonthYear = payrollDate.getMonth() === 11 ? payrollDate.getFullYear() + 1 : payrollDate.getFullYear();
    const nextMonth = new Date(nextMonthYear, nextMonthIndex, 1).toLocaleDateString(dateLocale, { month: 'long' });

    // Calculate days until closure
    const nextFifth = new Date(nextMonthYear, nextMonthIndex, 5);
    const diffTime = nextFifth - today;
    const daysUntilClosure = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
    const isClosingOverdue = diffTime < 0;
    const daysInMonth = new Date(payrollDate.getFullYear(), payrollDate.getMonth() + 1, 0).getDate();
    return {
      currentMonth,
      currentMonthLabel,
      fullDate,
      nextMonth,
      daysUntilClosure,
      isClosingOverdue,
      daysInMonth
    };
  }, [shownMonth, dateLocale]);

  // État de la paie du mois affiché, tant que les chiffres reçus sont bien les siens.
  const payrollStatus = stats.month === shownMonth ? stats.payrollStatus : null;
  const isPayrollClosed = payrollStatus === 'closed';

  const handleNavigate = (path) => {
    navigate(path);
  };

  const data = {
    labels: [t('home.salaryPaid'), t('home.employerCharges'), t('home.employeeCharges')],
    datasets: [
      {
        label: t('home.distribution'),
        data: [stats.totalPayrollCost, stats.total_cotisation_patronale, stats.total_cotisation_salariale],
        backgroundColor: ['#FFB3B3', '#A5F0C5', '#FFEB85'],
        borderWidth: 1,
      },
    ],
  };

  useEffect(() => {
    // Attendre le mois de paie actif : sans lui, on chargerait un mois pour rien.
    if (!dashboardMonth) return undefined;
    let cancelled = false;
    apiService.getElement(`/dashboard_stats?month=${dashboardMonth}`)
    .then((response) => {
      // Un clic rapide sur les flèches : seule la réponse du dernier mois s'affiche.
      if (cancelled) return;
      const { totalEmployees, maleEmployees, femaleEmployees, currentAbsences, totalPayrollCost,
         total_brut, total_salariale_globale_prev_month, total_impots, total_cotisation_salariale, total_cotisation_patronale,
         payslipCount, closedPayslipCount, payrollStatus, month, employeesWithoutContract } = response.data.dashboard_stats;
      setStats({
        totalEmployees,
        maleEmployees,
        femaleEmployees,
        currentAbsences,
        totalPayrollCost,
        total_brut,
        total_salariale_globale_prev_month,
        total_impots,
        total_cotisation_salariale,
        total_cotisation_patronale,
        payslipCount,
        closedPayslipCount,
        payrollStatus,
        month,
        employeesWithoutContract
      });
    }
    )
    .catch((error) => {
      console.error('Error fetching stats:', error);
    });
    return () => { cancelled = true; };
  }
  , [dashboardMonth]);

  const calculateTotalCost = () => {
    return (
      parseFloat(stats.totalPayrollCost || 0) +
      parseFloat(stats.total_cotisation_salariale || 0) +
      parseFloat(stats.total_cotisation_patronale || 0)
    );
  };

  const claculateDelta = () => {
    const currentCost = calculateTotalCost();
    const previousCost = parseFloat(stats.total_salariale_globale_prev_month || 0);
    const delta = previousCost ? ((currentCost - previousCost) / previousCost) * 100 : 0;
    return delta.toFixed(2);
  };

  return (
    <div className="">
      <div className="mx-auto px-2 ">
        
        <header>
          <h1 className="text-4xl font-bold">{t('home.welcome')}</h1>
          <p className="text-gray-600 mb-6">{t('home.todayIs', { date: dateInfo.fullDate })}</p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {/* Section principale */}
          <div className="md:col-span-9 gap-4">
            <div className="bg-white rounded-lg shadow-md px-6 py-3 mb-6">
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h2 className="text-2xl font-bold" data-cy="payroll-banner-title">{t('home.yourPayrollOf', { month: dateInfo.currentMonthLabel })}</h2>
                  {isPayrollClosed && (
                    <p className="text-sm text-green-700 mt-1" data-cy="payroll-banner-status">
                      {t('home.payrollClosedNotice', { month: dateInfo.currentMonthLabel, count: stats.payslipCount })}
                    </p>
                  )}
                  {payrollStatus === 'partial' && (
                    <p className="text-sm text-orange-600 mt-1" data-cy="payroll-banner-status">
                      {t('home.payrollPartialNotice', { month: dateInfo.currentMonthLabel, closed: stats.closedPayslipCount, count: stats.payslipCount })}
                    </p>
                  )}
                  {payrollStatus === 'none' && (
                    <p className="text-sm text-gray-500 mt-1" data-cy="payroll-banner-status">
                      {t('home.payrollNoneNotice', { month: dateInfo.currentMonthLabel })}
                    </p>
                  )}
                  {isCurrentMonthLocked && shownMonth === activePayrollMonth && (
                    <p className="text-sm text-orange-600 mt-1">
                      {t('home.monthLocked', { month: formatMonthLabel(activePayrollMonth) })}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  {/* État pas encore reçu pour ce mois : rien, plutôt qu'une échéance qui clignote. */}
                  {payrollStatus === null ? null : isPayrollClosed ? (
                    <span className="flex items-center gap-2 rounded-full bg-green-100 text-green-800 px-3 py-1 font-semibold" data-cy="payroll-banner-closed">
                      <FaCheckCircle size={16} aria-hidden="true" />
                      {t('home.payrollClosedBadge')}
                    </span>
                  ) : dateInfo.isClosingOverdue ? (
                    payrollStatus !== 'none' && (
                      <span className="flex items-center gap-2 text-orange-600 font-semibold" data-cy="payroll-banner-overdue">
                        <FaClock size={20} aria-hidden="true" />
                        {t('home.closingOverdue')}
                      </span>
                    )
                  ) : (
                  <div className="flex items-center">
                    <span className="inline-block mr-2" aria-hidden="true">
                      <FaClock size={20} />
                    </span>
                    <span className="text-gray-600">{t('home.closingIn')}</span>
                    <span className="font-semibold ml-2">
                      {t('home.daysCount', { count: dateInfo.daysUntilClosure })}
                    </span>
                  </div>
                  )}
                  <button
                    onClick={() => handleNavigate("/payslip/report")}
                    className="bg-[--color-primary] text-white px-4 py-2 rounded-full flex items-center">
                    {t('home.managePayroll')}
                    <FaArrowRight className="ml-2" size={14} aria-hidden="true" />
                  </button>
                </div>
              </div>

              <div className="bg-gray-200 rounded-full h-2 mb-4">
                <div
                  className="bg-[--color-primary] h-2 rounded-full"
                  style={{ width: isPayrollClosed ? '100%' : `${Math.min(100, Math.max(0, (dateInfo.daysInMonth + 5 - dateInfo.daysUntilClosure) / (dateInfo.daysInMonth+5) * 100))}%` }}
                ></div>
              </div>

              {/* Étapes de la paie */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex gap-4">
                  <div className="flex flex-col items-baseline mb-1">
                    <span className="text-xl font-bold mr-2">5</span>
                    <span className="text-gray-500 text-sm">{dateInfo.currentMonth}</span>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{t('home.payrollOpening')}</h3>
                    <p className="text-gray-500 text-sm">{t('home.payrollOpeningDetail', { month: dateInfo.currentMonth })}</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex flex-col items-baseline mb-1">
                    <span className="text-xl font-bold mr-2">15</span>
                    <span className="text-gray-500 text-sm">{dateInfo.currentMonth}</span>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{t('home.payslipPreparation')}</h3>
                    <p className="text-gray-500 text-sm">{t('home.payslipPreparationDetail')}</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex flex-col items-baseline mb-1">
                    <span className="text-xl font-bold mr-2">5</span>
                    <span className="text-gray-500 text-sm">{dateInfo.nextMonth}</span>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{t('home.payrollClosing')}</h3>
                    <p className="text-gray-500 text-sm">{t('home.payrollClosingDetail')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Statistiques du mois affiché par le calendrier */}
            <div className="flex items-baseline justify-between gap-4 mb-3">
              <h2 className="text-xl font-bold" data-cy="dashboard-stats-month">
                {t('home.statsOf', { month: formatMonthLabel(shownMonth) })}
              </h2>
              {stats.payslipCount === 0 && (
                <p className="text-sm text-gray-500" data-cy="dashboard-no-payroll">{t('home.noPayrollForMonth')}</p>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-lg p-3 space-y-2 pb-2">
                <p className="text-3xl font-bold">{stats.totalEmployees}</p>
                <p className="font-bold text-[--color-primary]">{t('home.employees')}</p>
                <div className="flex items-center gap-2">
                  <div className="p-1 bg-[#FFEB85] rounded-lg">
                    <AiOutlineWoman size={20} className="text-[--color-primary]" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{stats.femaleEmployees}</p>
                    <p className="text-sm font-semibold text-[--color-primary]">{t('home.women')}</p>
                  </div>
                  <div className="p-1 bg-[#C9FFFB] rounded-lg">
                    <AiOutlineMan size={20} className="text-[--color-primary]" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{stats.maleEmployees}</p>
                    <p className="text-sm font-semibold text-[--color-primary]">{t('home.men')}</p>
                  </div>
                </div>
                <NoContractAlert
                  count={stats.month === shownMonth ? stats.employeesWithoutContract : 0}
                  isCurrentMonth={isShowingCurrentMonth}
                  showEmployeesLink={canViewEmployees()}
                />
              </div>
              <div className="bg-white rounded-lg p-3 space-y-2">
                <p className="text-3xl font-bold">{stats.currentAbsences}</p>
                <p className="font-bold text-[--color-primary]">{isShowingCurrentMonth ? t('home.absent') : t('home.absentInMonth')}</p>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500">{t('home.absenceRate')}</p>
                  <span className="bg-[#28C76F29] text-[--color-primary] px-2 py-1 rounded-lg text-sm font-semibold">{stats.totalEmployees > 0 ? ((stats.currentAbsences / stats.totalEmployees) * 100).toFixed() + '%' : '0%'}</span>
                </div>
              </div>
              <div className="rounded-lg p-3 bg-gradient-to-b from-[#FFFCEA] to-[#FFFFFF] space-y-2">
                <div className="flex justify-end">
                  <p className="text-sm text-[--color-primary] font-bold">{claculateDelta()}%</p>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-2">
                    <p className="text-3xl text-[--color-primary] font-bold">
                      {calculateTotalCost().toLocaleString(dateLocale)}
                    </p>
                    <span className="text-[--color-primary] text-sm">F CFA</span>
                  </div>
                </div>
                <p className="font-bold text-[--color-primary]">{t('home.totalPayrollCost')}</p>
                <p className="text-sm text-gray-500">{t('home.lastMonth', { amount: stats.total_salariale_globale_prev_month.toLocaleString(dateLocale) })}</p>
              </div>
            </div>

            {/* Graphique et repartition */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white col-span-2 rounded-lg p-6 shadow-md">
                <h3 className="font-bold text-center text-xl mb-8">{t('home.chargesDistribution')}</h3>
                <div className='flex items-center gap-4'>
                  <div className="w-1/2 h-64 flex flex-col items-center justify-center relative">
                    <Doughnut
                      data={data}
                      options={{
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: false, // Désactiver les légendes générées automatiquement
                          },
                        },
                      }}
                    />
                    
                  </div>
                  {/* Légendes personnalisées avec montants */}
                  <div className="flex flex-col gap-6">
                    {/* Affichage de la somme totale sous le cercle */}
                    <div className="text-center w-full">
                      <span className="font-bold text-lg text-gray-700">
                        {t('home.total', {
                          amount: data.datasets[0].data
                            .map(val => Number(val) || 0)
                            .reduce((a, b) => a + b, 0)
                            .toLocaleString(dateLocale)
                        })}
                      </span>
                    </div>
                    {data.labels.map((label, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: data.datasets[0].backgroundColor[index] }}
                        ></span>
                        <span className=" text-gray-600">{label}</span>
                        <span className=" font-semibold text-gray-800">
                          {(Number(data.datasets[0].data[index]) || 0).toLocaleString(dateLocale)} F CFA
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-md">
                <h3 className="font-bold text-xl mb-16">{t('home.salaryDistribution')}</h3>
                <div className=" text-gray-600 space-y-4">
                  <p className="flex justify-between">
                    {t('home.totalGross')} <span className="font-bold text-gray-800">{stats.total_brut.toLocaleString(dateLocale)} F CFA</span>
                  </p>
                  <p className="flex justify-between items-center">
                    <span>
                      {t('home.totalEmployeeContribution')}<br />{t('home.employeeLabel')}
                    </span>
                    <span className="font-bold text-gray-800 break-all">{stats.total_cotisation_salariale.toLocaleString(dateLocale)} F CFA</span>
                  </p>
                  <p className="flex justify-between items-center">
                    <span>
                      {t('home.totalEmployeeContribution')}<br />{t('home.employerLabel')}
                    </span>
                    <span className="font-bold text-gray-800 break-all">{stats.total_cotisation_patronale.toLocaleString(dateLocale)} F CFA</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-3 flex flex-col h-full">
            {/* Calendrier */}
            <div className="bg-white p-1 rounded-lg overflow-visible">
              {/* <FullCalendar
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                locale="fr"
                firstDay={1}
                headerToolbar={{
                  start: 'prev',
                  center: 'title',
                  end: 'next',
                }}
                selectable={true}
                events={selectedDates} 
                height={"auto"}
                contentHeight={"auto"}
              /> */}
              <MyCalendar month={shownMonth} onMonthChange={setDashboardMonth} />
            </div>
            {upcomingEvents.length > 0 && (
              <div className="bg-white rounded-2xl p-4 mt-6 shadow-md flex-1 flex flex-col overflow-hidden">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">{isShowingCurrentMonth ? t('home.upcomingEvents') : t('home.monthEvents')}</h3>
                    {isShowingCurrentMonth && <p className="text-xs text-gray-500">{t('home.upcomingEventsSubtitle')}</p>}
                  </div>
                </div>

                <div className="space-y-2 flex-1 overflow-y-auto pr-1 max-h-[200px]">
                  {upcomingEvents.map((event, index) => {
                    const isSoon = event.daysUntil <= 2;
                    const dateLabel = formatEventDate(event.eventDate);
                    const timeLabel = event.daysUntil < 0
                      ? t('home.past')
                      : event.daysUntil === 0
                        ? t('home.today')
                        : event.daysUntil === 1
                          ? t('home.tomorrow')
                          : t('home.inDays', { count: event.daysUntil });

                    return (
                      <div
                        key={`${event.title}-${index}`}
                        className={`group flex items-center gap-3 rounded-xl border px-3 py-2 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm ${isSoon ? 'border-[--color-primary]/20 bg-[--bg-color]' : 'border-gray-100 bg-gray-50'}`}
                      >
                        <div
                          className="h-8 w-1.5 rounded-full shrink-0"
                          style={{ backgroundColor: event.color }}
                        />

                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-semibold text-slate-800 leading-tight truncate text-sm">
                              {event.title}
                            </p>
                            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${isSoon ? 'bg-white text-[--color-primary]' : 'bg-white text-gray-500'}`}>
                              {timeLabel}
                            </span>
                          </div>
                          <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                            <span className="text-xs">{dateLabel}</span>
                            {event.daysUntil >= 0 && (
                              <span className="h-1 w-1 rounded-full bg-gray-300" aria-hidden="true" />
                            )}
                            {event.daysUntil >= 0 && <span className="text-xs">{event.start}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Accès rapides */}
        <div className=" pt-8 mb-8">
          <h2 className="text-2xl font-bold mb-4">{t('home.quickAccess')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {canEditHrData() && (
            <button
              type="button"
              className="bg-white rounded-lg p-6 cursor-pointer border border-[--color-primary] text-left"
              onClick={() => handleNavigate("/primes")}
            >
              <div className="flex items-center gap-4 mb-4">
                <FaCoins  size={20} className="text-[--color-primary]" aria-hidden="true" />
                <h3 className="font-bold text-[--color-primary]">{t('home.remunerationElements')}</h3>
              </div>

              <p className="text-sm text-gray-600">{t('home.remunerationElementsDetail')}</p>

              <div className="flex bg-[--bg-color] justify-center p-1 w-[100px] items-center gap-2 mt-4 text-[--color-orange] text-sm font-semibold">
                <span>{t('home.seeMore')}</span>
                <FaArrowRight/>
              </div>
            </button>
            )}

            <button
              type="button"
              className="bg-white rounded-lg p-6 cursor-pointer border border-[--color-primary] text-left"
              onClick={() => handleNavigate("/payslip/report")}
            >
              <div className="flex items-center gap-4 mb-4">
                <FaFileAlt size={20} className="text-[--color-primary]" aria-hidden="true" />
                <h3 className="font-bold text-[--color-primary]">{t('home.payslip')}</h3>
              </div>
              <p className="text-sm text-gray-600">
                {isCurrentMonthLocked
                  ? t('home.payslipLockedDetail', { month: formatMonthLabel(activePayrollMonth) })
                  : t('home.payslipDetail', { month: formatMonthLabel(activePayrollMonth) })}
              </p>
              <div className="flex bg-[--bg-color] justify-center p-1 w-[100px] items-center gap-2 mt-4 text-[--color-orange] text-sm font-semibold">
                <span>{t('home.seeMore')}</span>
                <FaArrowRight/>
              </div>
            </button>

            {canViewEmployees() && (
            <button
              type="button"
              className="bg-white rounded-lg p-6 cursor-pointer border border-[--color-primary] text-left"
              onClick={() => handleNavigate("/employees")}
            >
              <div className="flex items-center gap-4 mb-4">
                <FaUsers size={20} className="text-[--color-primary]" aria-hidden="true" />
                <h3 className="font-bold text-[--color-primary]">{t('home.myEmployees')}</h3>
              </div>
              <p className="text-sm text-gray-600">{t('home.myEmployeesDetail')}</p>
              <div className="flex bg-[--bg-color] justify-center p-1 w-[100px] items-center gap-2 mt-4 text-[--color-orange] text-sm font-semibold">
                <span>{t('home.seeMore')}</span>
                <FaArrowRight/>
              </div>
            </button>
            )}

            {canAccessSettings() && (
            <button
              type="button"
              className="bg-white rounded-lg p-6 cursor-pointer border border-[--color-primary] text-left"
              onClick={() => handleNavigate("/settings")}
            >
              <div className='flex items-center gap-4 mb-4'>
                <FaCog size={20} className="text-[--color-primary]" aria-hidden="true" />
                <h3 className="font-bold text-[--color-primary]">{t('home.settings')}</h3>
              </div>
              <p className="text-sm text-gray-600">{t('home.settingsDetail')}</p>
              <div className="flex bg-[--bg-color] justify-center p-1 w-[100px] items-center gap-2 mt-4 text-[--color-orange] text-sm font-semibold">
                <span>{t('home.seeMore')}</span>
                <FaArrowRight/>
              </div>
            </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;
